import { inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { signalStoreFeature, type, withState } from '@ngrx/signals';
import { Events, withEventHandlers, withReducer } from '@ngrx/signals/events';
import { immerOn } from 'ngrx-immer/signals';
import { mapResponse } from '@ngrx/operators';
import {
  concatMap,
  delay,
  EMPTY,
  exhaustMap,
  filter,
  fromEvent,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { AUTH_SERVICE, authEvents } from '@kikkz/shared/auth/util';
import { AuthState } from '@kikkz/shared/auth/data-access';
import { LOGGING_SERVICE } from '@kikkz/shared/logging/util';
import {
  B04_LOGIN_MODE,
  b04AuthEvents,
  calculateExpiresAtFromNow,
  calculateRefreshDelay,
  deriveBayerClaims,
  IB04AuthService,
  isExpired,
} from '@kikkz/b04/auth/util';
import { AuthStorageService } from '../services/auth-storage.service';
import { ParentBridgeService } from '../services/parent-bridge.service';

const MINIMUM_STORAGE_RETRIEVAL_TIME_IN_MS = 500;

/**
 * B04 tenant login flow: the Bayer 04 OAuth2 (Authorization Code + PKCE) flow, including
 * token refresh scheduling and the embedded (iframe) parent-bridge backup path. Backend-user
 * sync is handled by `B04UserStore` in the b04 user domain, which reacts to the authenticated
 * token. Must be composed on top of `withAuthCore()` (which owns `AuthState`).
 */
export function withB04OAuth() {
  return signalStoreFeature(
    { state: type<AuthState>() },
    withState({ loginRequired: false }),
    withReducer(
      immerOn(b04AuthEvents.loginRequired, (state) => {
        state.loginRequired = true;
      }),
      immerOn(b04AuthEvents.initiateOAuthFlow, (state) => {
        state.loginRequired = false;
      }),
      immerOn(b04AuthEvents.exchangeTokensForCode, (state) => {
        state.isLoading = true;
        state.hasError = false;
      }),
      immerOn(b04AuthEvents.exchangeTokensForCodeSuccess, (state, { payload }) => {
        applyTokens(
          state,
          payload.response.id_token,
          payload.response.access_token,
          payload.response.refresh_token,
          payload.response.expires_in,
        );
        state.isLoading = false;
        state.isAuthenticated = true;
        state.loginRequired = false;
      }),
      immerOn(b04AuthEvents.exchangeTokensForCodeFailure, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.hasError = true;
      }),
      immerOn(b04AuthEvents.refreshToken, (state) => {
        state.isRefreshing = true;
        state.hasError = false;
      }),
      immerOn(b04AuthEvents.refreshTokenSuccess, (state, { payload }) => {
        applyTokens(
          state,
          payload.response.id_token,
          payload.response.access_token,
          payload.response.refresh_token,
          payload.response.expires_in,
        );
        state.isAuthenticated = true;
        state.isRefreshing = false;
        state.loginRequired = false;
      }),
      immerOn(b04AuthEvents.refreshTokenFailure, (state) => {
        state.isRefreshing = false;
        state.isAuthenticated = false;
        state.hasError = true;
      }),
      immerOn(b04AuthEvents.loadTokensFromStorageSuccess, (state, { payload }) => {
        const { tokens } = payload;
        state.idToken = tokens.id_token ?? null;
        state.accessToken = tokens.access_token ?? null;
        state.refreshToken = tokens.refresh_token ?? null;
        state.expiresAt = tokens.expiresAt;
        applyDerivedClaims(state, tokens.id_token);
        state.isAuthenticated =
          tokens.id_token !== null && tokens.access_token !== null && !isExpired(tokens.expiresAt);
        state.isLoading = false;
        state.loginRequired = false;
      }),
      immerOn(b04AuthEvents.requestTokensFromParent, (state) => {
        state.isLoading = true;
        state.hasError = false;
      }),
    ),
    withEventHandlers((store, events = inject(Events)) => {
      // provideB04Auth binds AUTH_SERVICE to B04AuthService, which implements IB04AuthService.
      const authService = inject(AUTH_SERVICE) as IB04AuthService;
      const storage = inject(AuthStorageService);
      const parentBridge = inject(ParentBridgeService);
      const logger = inject(LOGGING_SERVICE);
      const document = inject(DOCUMENT);
      const loginMode = inject(B04_LOGIN_MODE);
      // Automatic fallback when no token is available: redirect, or just report it in on-demand mode.
      const loginOrReport = () =>
        loginMode === 'on-demand' ? b04AuthEvents.loginRequired() : b04AuthEvents.initiateOAuthFlow();

      return {
        // --- App init ---
        initAuth$: events.on(authEvents.initAuth).pipe(
          map(() => {
            logger.info(`[Auth] init (${parentBridge.isEmbedded ? 'embedded' : 'standalone'})`);
            return b04AuthEvents.loadTokensFromStorage();
          }),
        ),

        // --- Standard OAuth (Authorization Code + PKCE) ---
        initiateOAuthFlow$: events.on(b04AuthEvents.initiateOAuthFlow).pipe(
          tap(() => void authService.initiateOAuthFlow()),
          switchMap(() => EMPTY),
        ),
        exchangeCodeForToken$: events.on(b04AuthEvents.exchangeTokensForCode).pipe(
          concatMap(({ payload }) =>
            authService.exchangeCodeForToken$(payload.code, payload.state).pipe(
              mapResponse({
                next: (response) => b04AuthEvents.exchangeTokensForCodeSuccess({ response }),
                error: (error: unknown) => {
                  logger.error('[Auth] Error exchanging tokens for code');
                  logger.error(String(error));
                  return b04AuthEvents.exchangeTokensForCodeFailure();
                },
              }),
            ),
          ),
        ),
        onExchangeTokensForCodeSuccess$: events.on(b04AuthEvents.exchangeTokensForCodeSuccess).pipe(
          map(({ payload }) => {
            const expiresAt = calculateExpiresAtFromNow(payload.response.expires_in);
            storage.clearSessionVariables();
            storage.saveAuthTokens({ ...payload.response, expiresAt });
            return b04AuthEvents.scheduleTokenRefresh();
          }),
        ),

        // --- Refresh ---
        refreshToken$: events.on(b04AuthEvents.refreshToken).pipe(
          exhaustMap(() => {
            const refreshToken = store.refreshToken();
            if (!refreshToken) {
              logger.warn('[Auth] Refresh failed: no refresh token available.');
              return of(b04AuthEvents.refreshTokenFailure());
            }
            return authService.refreshToken$(refreshToken).pipe(
              mapResponse({
                next: (response) => b04AuthEvents.refreshTokenSuccess({ response }),
                error: () => {
                  logger.warn('[Auth] Refresh failed.');
                  return b04AuthEvents.refreshTokenFailure();
                },
              }),
            );
          }),
        ),
        refreshTokenSuccess$: events.on(b04AuthEvents.refreshTokenSuccess).pipe(
          map(({ payload }) => {
            const expiresAt = calculateExpiresAtFromNow(payload.response.expires_in);
            storage.saveAuthTokens({ ...payload.response, expiresAt });
            return b04AuthEvents.scheduleTokenRefresh();
          }),
        ),
        refreshTokenFailure$: events.on(b04AuthEvents.refreshTokenFailure).pipe(
          tap(() => {
            logger.warn('[Auth] Refresh failed. Re-initiating OAuth flow.');
            storage.clearAuthTokens();
          }),
          delay(1000),
          map(() => loginOrReport()),
        ),
        scheduleTokenRefresh$: events.on(b04AuthEvents.scheduleTokenRefresh).pipe(
          switchMap(() => {
            const expiresAt = store.expiresAt();
            if (!expiresAt) {
              logger.warn('[Auth] No expiresAt found.');
              return of(b04AuthEvents.refreshToken());
            }
            const refreshInMs = calculateRefreshDelay(expiresAt);
            if (refreshInMs == null) {
              return of(b04AuthEvents.refreshToken());
            }
            return timer(refreshInMs).pipe(
              map(() => b04AuthEvents.refreshToken()),
              takeUntil(
                events.on(
                  b04AuthEvents.scheduleTokenRefresh,
                  b04AuthEvents.exchangeTokensForCodeFailure,
                  authEvents.logout,
                  b04AuthEvents.refreshToken,
                ),
              ),
            );
          }),
        ),

        // --- Token storage ---
        loadTokensFromStorage$: events.on(b04AuthEvents.loadTokensFromStorage).pipe(
          concatMap(() =>
            timer(MINIMUM_STORAGE_RETRIEVAL_TIME_IN_MS).pipe(
              take(1),
              map(() => {
                const tokens = storage.getAuthTokens();
                if (tokens) {
                  logger.info('[Auth] Loaded tokens from storage.');
                  return b04AuthEvents.loadTokensFromStorageSuccess({ tokens });
                }
                logger.info('[Auth] No tokens found in storage.');
                return b04AuthEvents.loadTokensFromStorageFailure();
              }),
            ),
          ),
        ),
        loadTokensFromStorageFailure$: events.on(b04AuthEvents.loadTokensFromStorageFailure).pipe(
          map(() => {
            if (parentBridge.isEmbedded) {
              logger.info('[Auth] No tokens in storage. Requesting backup from parent...');
              return b04AuthEvents.requestTokensFromParent();
            }
            return loginOrReport();
          }),
        ),
        loadTokensFromStorageSuccess$: events.on(b04AuthEvents.loadTokensFromStorageSuccess).pipe(
          mergeMap(() => {
            const expiresAt = store.expiresAt();
            if (!expiresAt) {
              logger.warn('[Auth] No expiresAt found in tokens.');
              return of(b04AuthEvents.refreshToken());
            }
            if (new Date() < new Date(expiresAt)) {
              return of(b04AuthEvents.scheduleTokenRefresh());
            }
            return of(b04AuthEvents.refreshToken());
          }),
        ),

        // --- Embedded (iframe) flow ---
        requestTokensFromParent$: events.on(b04AuthEvents.requestTokensFromParent).pipe(
          switchMap(() =>
            parentBridge.requestTokensFromParent$().pipe(
              map((tokens) => {
                if (tokens) {
                  storage.saveAuthTokens(tokens);
                  return b04AuthEvents.loadTokensFromStorageSuccess({ tokens });
                }
                return b04AuthEvents.requestTokensFromParentFailure();
              }),
            ),
          ),
        ),
        requestTokensFromParentFailure$: events.on(b04AuthEvents.requestTokensFromParentFailure).pipe(
          map(() => {
            logger.info('[Auth] No backup tokens from parent. Falling back to login.');
            return loginOrReport();
          }),
        ),
        syncTokensToParent$: events
          .on(b04AuthEvents.exchangeTokensForCodeSuccess, b04AuthEvents.refreshTokenSuccess)
          .pipe(
            tap(({ payload }) => {
              if (parentBridge.isEmbedded) {
                const expiresAt = calculateExpiresAtFromNow(payload.response.expires_in);
                parentBridge.sendTokensToParent({ ...payload.response, expiresAt });
              }
            }),
            switchMap(() => EMPTY),
          ),

        // --- Refresh on tab focus ---
        onVisibilityChange$: fromEvent(document, 'visibilitychange').pipe(
          filter(() => document.visibilityState !== 'hidden' && store.isAuthenticated()),
          map(() => b04AuthEvents.scheduleTokenRefresh()),
        ),
      };
    }),
  );
}

function applyTokens(
  state: AuthState,
  idToken: string,
  accessToken: string,
  refreshToken: string | undefined,
  expiresIn: number,
): void {
  state.idToken = idToken ?? null;
  state.accessToken = accessToken ?? null;
  state.refreshToken = refreshToken ?? null;
  state.expiresAt = calculateExpiresAtFromNow(expiresIn);
  applyDerivedClaims(state, idToken);
}

function applyDerivedClaims(state: AuthState, idToken: string | null): void {
  const claims = deriveBayerClaims(idToken);
  state.userName = claims.userName;
  state.accessDenied = claims.accessDenied;
  state.belongsToAdRestrictedGroup = claims.belongsToAdRestrictedGroup;
}
